import { getCookie } from "cookies-next";
import { useTranslation } from "next-i18next";
import { useEffect, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/router";

import { PREPR_AUTH_USER_COOKIE_NAME } from "~config/auth";
import classnames from "~utils/classnames/classnames";

import { useContentRoutes } from "~contexts/ContentRoutesContext";
import useLogout from "~hooks/useLogout";
import IconButtonVisual from "~ts_components/Buttons/IconButtonVisual/IconButtonVisual";
import PrimaryButtonVisual from "~ts_components/Buttons/PrimaryButtonVisual/PrimaryButtonVisual";
import DuoColorText from "~ts_components/DuoColorText/DuoColorText";
import Icon from "~ts_components/Icon/Icon";
import {
  PopoverContent,
  PopoverProvider,
  PopoverTrigger,
} from "~ts_components/Popover/Popover";

const HeaderAuthButton = () => {
  const [user, setUser] = useState(null);
  const isLoggedIn = user && user.first_name ? true : false;
  const [isOpen, setOpen] = useState(false);

  const { t } = useTranslation("common");
  const router = useRouter();
  const { logout } = useLogout();
  const { loginPageRoute, accountPageRoute, accountSettingsPageRoute } =
    useContentRoutes();

  async function onLogout() {
    await logout();
    router.reload();
  }

  useEffect(() => {
    const cookie = getCookie(PREPR_AUTH_USER_COOKIE_NAME);
    if (cookie) {
      setUser(JSON.parse(cookie));
    }
  }, []);

  if (!isLoggedIn && loginPageRoute) {
    return (
      <Link href={loginPageRoute}>
        <IconButtonVisual iconName={"userCross"} variant="outline" size="md" />
      </Link>
    );
  }

  return (
    <PopoverProvider isOpen={isOpen} onOpenChange={setOpen}>
      <PopoverTrigger className="cursor-pointer">
        <PrimaryButtonVisual
          startIcon="userCheck"
          startIconClassName="size-5"
          endIcon="chevronUp"
          endIconClassName={classnames(
            "size-5 transition-transform duration-200",
            isOpen && "rotate-180",
          )}
          variant="outline"
          color="grey"
          size="sm"
          className="max-lg:hidden"
        >
          {user?.first_name}
        </PrimaryButtonVisual>
        <IconButtonVisual
          iconName="userCheck"
          variant="outline"
          size="md"
          className="lg:hidden"
        />
      </PopoverTrigger>
      <PopoverContent
        offset={14}
        className="drop-shadow-xs border-grey-border w-full max-w-80 rounded-lg border bg-white p-8"
      >
        <div className="flex flex-col gap-4">
          <p className="text-header-4">
            <DuoColorText
              text={t("loginMenu.title", {
                name: user?.first_name,
              })}
              emphasis={user?.first_name}
            />
          </p>
          <nav>
            <ul className="flex flex-col gap-2 *:border-b *:border-gray-200 *:py-2">
              {accountPageRoute && (
                <li>
                  <Link
                    href={accountPageRoute}
                    className="group flex items-center justify-between gap-2"
                  >
                    <p className="group-hover:underline">
                      {t("loginMenu.uitpas")}
                    </p>
                    <Icon name="ticket" iconClassName="size-4" />
                  </Link>
                </li>
              )}
              {accountSettingsPageRoute && (
                <li>
                  <Link
                    href={accountSettingsPageRoute}
                    className="group flex items-center justify-between gap-2"
                  >
                    <p className="group-hover:underline">
                      {t("loginMenu.preferences")}
                    </p>
                    <Icon name="gear" iconClassName="size-4" />
                  </Link>
                </li>
              )}
              <li>
                <button
                  onClick={onLogout}
                  className="group flex w-full cursor-pointer items-center justify-between gap-2"
                >
                  <p className="group-hover:underline">
                    {t("loginMenu.logout")}
                  </p>
                  <Icon name="logout" iconClassName="size-4" />
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </PopoverContent>
    </PopoverProvider>
  );
};

export default HeaderAuthButton;
